'use client'

import { Fragment, useState, type MouseEventHandler } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import {
  ClipboardPlus,
  ClipboardList,
  Palette,
  type LucideIcon,
  UserRound,
  Users,
} from 'lucide-react'
import { CLIENT_PAGES } from '@/components/client/constants/pages'
import { CLIENT_KIT_REGISTRATION_FIRST_STEP_PATH } from '@/components/client/kit-registration/flow'
import StartNewModal from '@/components/client/navigation/StartNewModal'
import { useIsSuperAdmin } from '@/hooks/useIsSuperAdmin'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import { START_NEW_KIT_REGISTRATION_EVENT } from '@/utilities/orders/events'
import { cn } from '@/utilities/ui'
import { isPathActive } from '@/utilities/navigation'
import { isDashboardNavPath } from '@/utilities/dashboard/dashboardContext'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

type MobileNavItem = {
  id: 'dashboard' | 'orders' | 'myProfile'
  label: string
  href: string
  Icon: LucideIcon
}

type NavMenuMobileProps = {
  items: readonly MobileNavItem[]
  pathname: string
  canAccessKitRegistration?: boolean
  isProgramScopedNavDisabled?: boolean
}

const ORDERS_DISABLED_TOOLTIP = 'Select a program to view orders'
const REGISTER_KIT_DISABLED_TOOLTIP = 'Select a program to register a kit'
const PROFILE_DISABLED_TOOLTIP = 'Select a program to view profile and settings'

const FOCUS_VISIBLE_RING =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cp-bright-blue'

const DISABLED_NAV_BUTTON_CLASS = cn(
  'flex min-w-0 flex-1 cursor-not-allowed justify-center border-0 bg-transparent p-0',
  FOCUS_VISIBLE_RING,
)

function DisabledMobileProfileItem({
  label,
  Icon,
  testId,
}: Readonly<{ label: string; Icon: LucideIcon; testId: string }>) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex w-full cursor-not-allowed items-center gap-3 rounded-md border-0 bg-transparent px-3 py-3 text-sm text-cp-placeholder opacity-60',
              FOCUS_VISIBLE_RING,
            )}
            aria-disabled="true"
            aria-label={`${label}. ${PROFILE_DISABLED_TOOLTIP}`}
            data-testid={testId}
            data-disabled="true"
          >
            <Icon className="h-5 w-5 text-cp-placeholder" aria-hidden="true" />
            <span>{label}</span>
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-xs border-light-grey bg-white text-cp-dark-blue"
        >
          {PROFILE_DISABLED_TOOLTIP}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default function NavMenuMobile({
  items,
  pathname,
  canAccessKitRegistration = false,
  isProgramScopedNavDisabled = false,
}: Readonly<NavMenuMobileProps>) {
  const router = useRouter()
  const { isSuperAdmin, isSuperAdminCheckReady } = useIsSuperAdmin()
  const [isStartNewKitRegistrationModalOpen, setIsStartNewKitRegistrationModalOpen] =
    useState(false)
  const canViewThemeSettings = isSuperAdminCheckReady && isSuperAdmin
  const canViewEligibilityQuestionnaireBuilder = isSuperAdminCheckReady && isSuperAdmin
  const closeStartNewKitRegistrationModal = () => setIsStartNewKitRegistrationModalOpen(false)
  const profileMenuItems = [
    {
      id: 'my-profile',
      label: 'My Profile',
      href: CLIENT_PAGES.myProfile,
      Icon: UserRound,
    },
    {
      id: 'users',
      label: 'Users',
      href: CLIENT_PAGES.users,
      Icon: Users,
    },
    ...(canViewThemeSettings
      ? [
          {
            id: 'theme',
            label: 'Theme',
            href: CLIENT_PAGES.theme,
            Icon: Palette,
          },
        ]
      : []),
    ...(canViewEligibilityQuestionnaireBuilder
      ? [
          {
            id: 'eligibility-questionnaire-builder',
            label: 'EQ Builder',
            href: CLIENT_PAGES.eligibilityQuestionnaireBuilder,
            Icon: ClipboardList,
          },
        ]
      : []),
  ]

  const isProfileSectionActive =
    isPathActive(pathname, CLIENT_PAGES.myProfile) ||
    isPathActive(pathname, CLIENT_PAGES.users) ||
    isPathActive(pathname, CLIENT_PAGES.theme) ||
    isPathActive(pathname, CLIENT_PAGES.eligibilityQuestionnaireBuilder)
  const isKitRegistrationActive = isPathActive(pathname, CLIENT_PAGES.kitRegistration)

  const handleKitRegistrationClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
    trackEvent('client:nav:register_kit_click', { source: 'bottom_nav' })
    if (!isKitRegistrationActive) return
    event.preventDefault()
    setIsStartNewKitRegistrationModalOpen(true)
  }

  const handleConfirmStartNewKitRegistration = () => {
    closeStartNewKitRegistrationModal()
    globalThis.dispatchEvent(new Event(START_NEW_KIT_REGISTRATION_EVENT))
    router.replace(CLIENT_KIT_REGISTRATION_FIRST_STEP_PATH)
  }

  const kitRegistrationContent = (
    <span
      className={cn(
        'flex flex-col items-center gap-1 text-xs leading-none',
        isProgramScopedNavDisabled
          ? 'cursor-not-allowed text-cp-placeholder opacity-50'
          : isKitRegistrationActive
            ? 'text-cp-bright-blue'
            : 'text-cp-placeholder',
      )}
    >
      <span
        className={cn(
          'h-1 w-1 rounded-full',
          !isProgramScopedNavDisabled && isKitRegistrationActive
            ? 'bg-cp-bright-blue'
            : 'bg-transparent',
        )}
        aria-hidden={isProgramScopedNavDisabled || !isKitRegistrationActive}
      />
      <ClipboardPlus absoluteStrokeWidth className="h-5 w-5" aria-hidden="true" />
      <span>Register</span>
    </span>
  )

  const showKitRegistrationLink = canAccessKitRegistration

  const kitRegistrationLink = showKitRegistrationLink ? (
    isProgramScopedNavDisabled ? (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className={DISABLED_NAV_BUTTON_CLASS}
              aria-disabled="true"
              aria-label={`Register. ${REGISTER_KIT_DISABLED_TOOLTIP}`}
              data-testid="client-bottom-menu-kit-registration"
              data-disabled="true"
            >
              {kitRegistrationContent}
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="max-w-xs border-light-grey bg-white text-cp-dark-blue"
          >
            {REGISTER_KIT_DISABLED_TOOLTIP}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      <Link
        href={CLIENT_KIT_REGISTRATION_FIRST_STEP_PATH}
        className="flex min-w-0 flex-1 justify-center"
        aria-current={isKitRegistrationActive ? 'page' : undefined}
        data-testid="client-bottom-menu-kit-registration"
        onClick={handleKitRegistrationClick}
      >
        {kitRegistrationContent}
      </Link>
    )
  ) : null

  return (
    <nav
      className={cn(
        'fixed bottom-0 left-0 right-0 z-30 flex items-center justify-between',
        'bg-white/30 px-8 py-4 shadow-[0_-1rem_2.5rem_rgba(19,11,64,0.32)] backdrop-blur-xl',
      )}
      aria-label="Client mobile navigation"
      data-testid="client-mobile-bottom-nav"
    >
      {items.map(({ id, label, href, Icon }) => {
        const disableOrders = isProgramScopedNavDisabled && id === 'orders'
        let isActive: boolean
        if (disableOrders) {
          isActive = false
        } else if (id === 'dashboard') {
          isActive = isDashboardNavPath(pathname)
        } else if (id === 'myProfile') {
          isActive = isProfileSectionActive
        } else {
          isActive = isPathActive(pathname, href)
        }
        const navItemContent = (
          <span
            className={cn(
              'flex flex-col items-center gap-1 text-xs leading-none',
              disableOrders
                ? 'cursor-not-allowed text-cp-placeholder opacity-50'
                : isActive
                  ? 'text-cp-bright-blue'
                  : 'text-cp-placeholder',
            )}
          >
            <span
              className={cn(
                'h-1 w-1 rounded-full',
                isActive ? 'bg-cp-bright-blue' : 'bg-transparent',
              )}
              aria-hidden={!isActive}
            />
            <Icon absoluteStrokeWidth className="h-5 w-5" aria-hidden="true" />
            <span>{label}</span>
          </span>
        )

        if (id === 'myProfile') {
          return (
            <PopoverPrimitive.Root key={id}>
              <PopoverPrimitive.Trigger asChild>
                <button
                  type="button"
                  className="flex min-w-0 flex-1 cursor-pointer justify-center border-0 bg-transparent p-0"
                  aria-current={isActive ? 'page' : undefined}
                  data-testid={`client-bottom-menu-${id}`}
                >
                  {navItemContent}
                </button>
              </PopoverPrimitive.Trigger>
              <PopoverPrimitive.Portal>
                <PopoverPrimitive.Content
                  align="end"
                  side="top"
                  sideOffset={16}
                  collisionPadding={16}
                  className={cn(
                    'z-50 w-56 rounded-lg border border-light-grey bg-white p-2 shadow-lg',
                    'data-[state=open]:animate-in data-[state=closed]:animate-out',
                    'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                  )}
                  data-testid="client-bottom-profile-menu"
                >
                  {profileMenuItems.map(({ id: profileItemId, label, href, Icon }) => {
                    const isProfileItemActive = isPathActive(pathname, href)
                    const testId = `client-bottom-profile-menu-${profileItemId}`
                    if (isProgramScopedNavDisabled) {
                      return (
                        <DisabledMobileProfileItem
                          key={profileItemId}
                          label={label}
                          Icon={Icon}
                          testId={testId}
                        />
                      )
                    }
                    return (
                      <PopoverPrimitive.Close asChild key={profileItemId}>
                        <Link
                          href={href}
                          className={cn(
                            'flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-3 text-sm text-cp-dark-blue',
                            'hover:bg-cp-special-grey',
                            isProfileItemActive && 'bg-cp-special-grey text-cp-bright-blue',
                          )}
                          aria-current={isProfileItemActive ? 'page' : undefined}
                          data-testid={testId}
                        >
                          <Icon className="h-5 w-5 text-cp-bright-blue" aria-hidden="true" />
                          <span>{label}</span>
                        </Link>
                      </PopoverPrimitive.Close>
                    )
                  })}
                </PopoverPrimitive.Content>
              </PopoverPrimitive.Portal>
            </PopoverPrimitive.Root>
          )
        }

        if (disableOrders) {
          return (
            <Fragment key={id}>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className={DISABLED_NAV_BUTTON_CLASS}
                      aria-disabled="true"
                      aria-label={`${label}. ${ORDERS_DISABLED_TOOLTIP}`}
                      data-testid={`client-bottom-menu-${id}`}
                      data-disabled="true"
                    >
                      {navItemContent}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="max-w-xs border-light-grey bg-white text-cp-dark-blue"
                  >
                    {ORDERS_DISABLED_TOOLTIP}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {kitRegistrationLink}
            </Fragment>
          )
        }

        const navLink = (
          <Link
            href={href}
            className="flex min-w-0 flex-1 justify-center"
            aria-current={isActive ? 'page' : undefined}
            data-testid={`client-bottom-menu-${id}`}
          >
            {navItemContent}
          </Link>
        )

        if (id === 'orders') {
          return (
            <Fragment key={id}>
              {navLink}
              {kitRegistrationLink}
            </Fragment>
          )
        }

        return <Fragment key={id}>{navLink}</Fragment>
      })}
      <StartNewModal
        variant={isStartNewKitRegistrationModalOpen ? 'kit-registration' : null}
        isSubmitting={false}
        onOpenChange={(open) => {
          if (!open) closeStartNewKitRegistrationModal()
        }}
        onCancel={closeStartNewKitRegistrationModal}
        onConfirm={handleConfirmStartNewKitRegistration}
      />
    </nav>
  )
}
